import { useEffect, useRef, useState } from 'react';
import { formatTwoKTime } from '../utils/twoKTime';
import {
  athleteWorkoutTarget,
  RATE_PRESETS,
  pickTarget,
  speedPercentForRate,
  suggestedRateForPercent,
  workoutTarget
} from '../utils/korzoChart';

function formatRate(rate) {
  if (rate == null || Number.isNaN(Number(rate))) return null;
  const n = Number(rate);
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function rateFromPercent(value) {
  const rate = suggestedRateForPercent(value);
  return rate == null ? '' : String(formatRate(rate));
}

function goalSpeedHeadline(percent) {
  const p = Number(percent);
  if (!Number.isFinite(p) || p <= 0) return '';
  const rate = formatRate(suggestedRateForPercent(p));
  return rate
    ? `${p.toFixed(1)}% of 2k speed · r${rate} on the chart`
    : `${p.toFixed(1)}% of 2k speed`;
}

function percentsMatch(a, b) {
  return a != null && b != null && Math.abs(a - b) < 0.051;
}

export function formatTargetLine(target, { includeClass = true, includeRate = true } = {}) {
  if (!target?.target_split_seconds) return '';
  const split = formatTwoKTime(target.target_split_seconds);
  const rate = includeRate ? formatRate(target.suggested_rate) : null;
  const klass = includeClass && target.boat_class ? ` (${target.boat_class})` : '';
  return `${split}${rate ? ` @ r${rate}` : ''}${klass}`;
}

export function KorzoTargetSummary({ targets = [], boatClass, className = '', includeRate = true }) {
  if (!targets.length) return null;
  const shown = boatClass ? [pickTarget(targets, boatClass)].filter(Boolean) : targets;
  if (shown.length === 0) return null;

  return (
    <p className={className}>
      {shown.map((target, idx) => (
        <span key={target.boat_class || idx} className={idx > 0 ? 'ml-2' : undefined}>
          {idx === 0 ? 'Your target: ' : '· '}
          <span className="font-mono">{formatTwoKTime(target.target_split_seconds)}</span>
          {includeRate && target.suggested_rate != null && ` @ r${formatRate(target.suggested_rate)}`}
          {target.boat_class ? ` (${target.boat_class})` : ''}
        </span>
      ))}
    </p>
  );
}

export function KorzoTargetPanel({
  goalPercent,
  boatClass,
  athletes = [],
  templateTargets = [],
  className = ''
}) {
  const percent = Number(goalPercent);
  if (!Number.isFinite(percent) || percent <= 0) return null;

  const people = (athletes || []).filter(Boolean);

  if (people.length > 0) {
    if (!boatClass) {
      return (
        <div className={`rounded-lg border border-indigo-200 bg-indigo-50 p-3 ${className}`}>
          <p className="text-sm font-medium text-indigo-900">
            {goalSpeedHeadline(percent)}
          </p>
          <p className="text-xs text-indigo-700 mt-1">Select a boat class to see each athlete’s target split.</p>
        </div>
      );
    }

    const rows = people.map((athlete) => ({
      id: athlete.id,
      name: athlete.name || athlete.athlete_name || `Athlete ${athlete.id}`,
      target: athleteWorkoutTarget(athlete, boatClass, percent)
    }));

    return (
      <div className={`rounded-lg border border-indigo-200 bg-indigo-50 p-3 ${className}`}>
        <p className="text-sm font-medium text-indigo-900 mb-2">
          Target split · {goalSpeedHeadline(percent)}
        </p>
        <div className="space-y-1">
          {rows.map((row) => (
            <div key={row.id} className="flex items-baseline justify-between gap-3 text-sm">
              <span className="text-indigo-800 truncate">{row.name}</span>
              {row.target ? (
                <span className="font-mono text-indigo-900 whitespace-nowrap">
                  {formatTargetLine(row.target, { includeClass: false, includeRate: false })}
                </span>
              ) : (
                <span className="text-xs text-indigo-600">No 2k goal set</span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!templateTargets.length) return null;
  const picked = boatClass ? pickTarget(templateTargets, boatClass) : null;
  const shown = boatClass ? (picked ? [picked] : []) : templateTargets;

  return (
    <div className={`rounded-lg border border-indigo-200 bg-indigo-50 p-3 ${className}`}>
      <p className="text-sm font-medium text-indigo-900">
        {goalSpeedHeadline(percent)}
      </p>
      {boatClass && shown.length === 0 && (
        <p className="text-xs text-indigo-700 mt-1">
          No 2k goal for this boat class. Add one on your profile, or a Default 2k, to see a target split.
        </p>
      )}
      {shown.length > 0 && (
        <div className="mt-1 space-y-0.5">
          {shown.map((target) => (
            <p key={target.boat_class} className="text-sm text-indigo-800">
              Your target: <span className="font-mono font-semibold">{formatTwoKTime(target.target_split_seconds)}</span>
              {target.boat_class ? ` (${target.boat_class})` : ''}
            </p>
          ))}
        </div>
      )}
      {!boatClass && templateTargets.length > 1 && (
        <p className="text-xs text-indigo-700 mt-1">Select a boat class to use the matching 2k goal.</p>
      )}
    </div>
  );
}

export function WorkoutTargetCard({
  goalPercent,
  targets = [],
  boatClass,
  promptForGoal = false,
  className = ''
}) {
  const percent = Number(goalPercent);
  if (!Number.isFinite(percent) || percent <= 0) return null;

  return (
    <div className={`rounded-lg border border-indigo-200 bg-indigo-50 p-3 ${className}`}>
      <p className="text-sm font-medium text-indigo-900">
        {goalSpeedHeadline(percent)}
      </p>
      {targets.length > 0 ? (
        <KorzoTargetSummary
          targets={targets}
          boatClass={boatClass}
          includeRate={false}
          className="text-sm text-indigo-800 mt-1"
        />
      ) : promptForGoal ? (
        <p className="text-xs text-indigo-700 mt-1">
          Add a 2k goal on your profile to see your split for this piece.
        </p>
      ) : null}
    </div>
  );
}

export function GoalPercentPicker({ value, onChange, hint }) {
  const valueKey = value == null ? '' : String(value);
  const [rateText, setRateText] = useState(() => rateFromPercent(value));
  const [percentText, setPercentText] = useState(() => valueKey);
  const lastSelfValue = useRef(valueKey);

  useEffect(() => {
    if (valueKey === lastSelfValue.current) return;
    lastSelfValue.current = valueKey;
    setPercentText(valueKey);
    setRateText(valueKey === '' ? '' : rateFromPercent(valueKey));
  }, [valueKey]);

  const commitPercent = (raw) => {
    lastSelfValue.current = raw;
    onChange(raw);
  };

  const applyRate = (raw) => {
    setRateText(raw);
    if (raw === '') {
      setPercentText('');
      commitPercent('');
      return;
    }
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 12 || n > 50) return;
    const percent = speedPercentForRate(n);
    if (percent == null) return;
    const next = String(percent);
    setPercentText(next);
    commitPercent(next);
  };

  const applyPercent = (raw) => {
    setPercentText(raw);
    if (raw === '') {
      commitPercent('');
      return;
    }
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 1 || n > 150) return;
    commitPercent(String(n));
  };

  const useChartPercent = () => {
    const rateNum = Number(rateText);
    const chartPercent = Number.isFinite(rateNum) ? speedPercentForRate(rateNum) : null;
    if (chartPercent == null) return;
    const next = String(chartPercent);
    setPercentText(next);
    commitPercent(next);
  };

  const rateNum = Number(rateText);
  const chartPercent = Number.isFinite(rateNum) && rateNum >= 12 ? speedPercentForRate(rateNum) : null;
  const chosenPercent = Number(percentText);
  const inferred = Number.isFinite(chosenPercent) && chosenPercent > 0 ? chosenPercent : null;
  const overridden = inferred != null && chartPercent != null && !percentsMatch(inferred, chartPercent);
  const displayRate = formatRate(rateNum) || (inferred != null ? formatRate(suggestedRateForPercent(inferred)) : null);

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Stroke rate</label>
          <input
            type="number"
            min="12"
            max="50"
            step="1"
            value={rateText}
            onChange={(e) => applyRate(e.target.value)}
            placeholder="e.g. 28"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-theme-primary focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">% of 2k</label>
          <input
            type="number"
            min="1"
            max="150"
            step="0.1"
            value={percentText}
            onChange={(e) => applyPercent(e.target.value)}
            placeholder="e.g. 90"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-theme-primary focus:border-transparent"
          />
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {RATE_PRESETS.map((preset) => (
          <button
            key={preset.rate}
            type="button"
            onClick={() => applyRate(String(preset.rate))}
            className={`text-xs px-2 py-1 rounded-full border ${
              String(rateText) === String(preset.rate)
                ? 'bg-indigo-100 border-indigo-300 text-indigo-800'
                : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {preset.label}
          </button>
        ))}
      </div>
      {chartPercent != null ? (
        <p className="text-xs text-indigo-800 mt-1.5">
          {displayRate ? `r${displayRate} suggests ${chartPercent.toFixed(1)}% of 2k. ` : ''}
          {overridden
            ? `Using ${inferred.toFixed(1)}% instead.`
            : inferred != null
              ? 'Using the chart percent.'
              : ''}
          {overridden && (
            <button
              type="button"
              onClick={useChartPercent}
              className="ml-1 underline text-indigo-700 hover:text-indigo-900"
            >
              Use chart %
            </button>
          )}
        </p>
      ) : inferred != null ? (
        <p className="text-xs text-indigo-800 mt-1.5">Using {inferred.toFixed(1)}% of 2k.</p>
      ) : null}
      {hint ? <p className="text-xs text-gray-500 mt-1">{hint}</p> : null}
    </div>
  );
}

const SAMPLE_2KS = [
  { label: '6:30', seconds: 390 },
  { label: '6:45', seconds: 405 },
  { label: '7:00', seconds: 420 },
  { label: '7:15', seconds: 435 },
  { label: '7:30', seconds: 450 }
];

export function KorzoGoalPreview({ goalPercent, className = '' }) {
  const percent = Number(goalPercent);
  if (!Number.isFinite(percent) || percent <= 0) return null;
  return (
    <div className={`mt-3 rounded-lg border border-indigo-100 bg-indigo-50/70 p-3 ${className}`}>
      <p className="text-sm text-indigo-900">
        {goalSpeedHeadline(percent)}
      </p>
      <table className="mt-2 w-full text-xs">
        <thead>
          <tr className="text-indigo-700">
            <th className="text-left font-medium py-0.5">2k goal</th>
            <th className="text-right font-medium py-0.5">Target split</th>
          </tr>
        </thead>
        <tbody>
          {SAMPLE_2KS.map((sample) => {
            const target = workoutTarget({ twoKSeconds: sample.seconds, goalPercent: percent });
            return (
              <tr key={sample.label} className="text-indigo-900">
                <td className="py-0.5">{sample.label}</td>
                <td className="py-0.5 text-right font-mono">
                  {target ? formatTwoKTime(target.target_split_seconds) : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
